import Badge from '@amzn/awsui-components-react/polaris/badge';
import darkAccessCodeIcon from 'assets/images/landing_page/dark_access_code.png';
import darkAwsAccountIcon from 'assets/images/landing_page/dark_aws_account.png';
import darkBrowseIcon from 'assets/images/landing_page/dark_browse.png';
import darkCompleteIcon from 'assets/images/landing_page/dark_complete.png';
import darkJoinEventIcon from 'assets/images/landing_page/dark_join_event.png';
import darkLaunchIcon from 'assets/images/landing_page/dark_launch.png';
import darkLearningCatalogIcon from 'assets/images/landing_page/dark_learning_catalog.png';
import lightAccessCodeIcon from 'assets/images/landing_page/light_access_code.png';
import lightAwsAccountIcon from 'assets/images/landing_page/light_aws_account.png';
import lightBrowseIcon from 'assets/images/landing_page/light_browse.png';
import lightCompleteIcon from 'assets/images/landing_page/light_complete.png';
import lightJoinEventIcon from 'assets/images/landing_page/light_join_event.png';
import lightLaunchIcon from 'assets/images/landing_page/light_launch.png';
import lightLearningCatalogIcon from 'assets/images/landing_page/light_learning_catalog.png';
import React from 'react';

interface BannerConfig {
    darkIcon: string;
    lightIcon: string;
    iconAltText: string;
    emphasisText: string;
    text: JSX.Element;
}

export interface ILandingPageConfig {
    config: {
        header: {
            title: string;
            description: string;
            subText: string;
        };
        callToAction: {
            title: string;
            subText: string;
            buttonText: string;
        };
        bannerConfig: {
            title: string;
            firstRow: {
                title: string;
                config: BannerConfig[];
            };
            secondRow: {
                title: string;
                config: BannerConfig[];
            };
        };
        benefitsAndFeatures: {
            title: string;
            features: {
                id: number;
                title: string;
                description: string;
            }[];
        };
        learningCatalog: {
            title: string;
            darkIcon: string;
            lightIcon: string;
            buttonText: string;
            iconAltText: string;
        };
        whatCanYouDoHere: {
            title: string;
            list: {
                detail: string;
            }[];
        };
    };
}

const LandingPageConfig: ILandingPageConfig = {
    config: {
        header: {
            title: 'AWS Workshop Studio',
            description: 'join hands on events and workshops',
            subText:
                'Find physical and virtual self-paced Workshops, GameDays, Bootcamps, Immersion Days, and other events that require hands-on access to AWS accounts.',
        },
        callToAction: {
            title: 'Join an event',
            subText:
                'All you need is an event access code to join and get started with your event',
            buttonText: 'Get started',
        },
        benefitsAndFeatures: {
            title: 'Benefits of using AWS Workshop Studio',
            features: [
                // TODO: add back when content catalog is available
                // {
                //     id: 0,
                //     title: 'Discoverability',
                //     description:
                //         "Easily browse and discover hands on content from a wide variety of topics such as Serverless or Machine Learning, or on specific AWS services like Amazon Kinesis or AWS Lambda.  Find exactly the right content you're looking for.",
                // },
                {
                    id: 1,
                    title: 'Self-paced or Facilitator Led',
                    description:
                        'Workshop content is publicly available.  Depending on your preferred engagement model, you can attend an AWS led event or you can use the content in a self-paced manner with your own AWS Account.',
                },
                {
                    id: 2,
                    title: 'Prepared Environments',
                    description:
                        'For facilitator led events, all prerequisites are automatically deployed into the provided AWS account.  This means you can immediately get started without having to wait, saving you valuable time.',
                },
                {
                    id: 3,
                    title: 'Updated Content',
                    description:
                        'Workshop authors are always adding new workshops and updating existing ones, so that you can get the most up to date content about new and existing AWS Services and Topics.',
                },
            ],
        },
        learningCatalog: {
            title: 'Learning Catalog',
            darkIcon: darkLearningCatalogIcon,
            lightIcon: lightLearningCatalogIcon,
            iconAltText: 'Learning Catalog, coming soon!',
            buttonText: 'Coming Soon!',
        },
        bannerConfig: {
            title: 'How AWS Workshop Studio Works',
            firstRow: {
                title: 'Scheduled events',
                config: [
                    {
                        darkIcon: darkAccessCodeIcon,
                        lightIcon: lightAccessCodeIcon,
                        iconAltText: 'Receive',
                        emphasisText: 'Receive',
                        text: <>event access code</>,
                    },
                    {
                        darkIcon: darkJoinEventIcon,
                        lightIcon: lightJoinEventIcon,
                        iconAltText: 'Join event',
                        emphasisText: 'Join event',
                        text: <>by entering the code</>,
                    },
                    {
                        darkIcon: darkAwsAccountIcon,
                        lightIcon: lightAwsAccountIcon,
                        iconAltText: 'Access',
                        emphasisText: 'Access',
                        text: <>provided AWS Account</>,
                    },

                    {
                        darkIcon: darkCompleteIcon,
                        lightIcon: lightCompleteIcon,
                        iconAltText: 'Complete',
                        emphasisText: 'Complete',
                        text: <>the workshop</>,
                    },
                ],
            },
            secondRow: {
                title: 'Self-paced workshops',
                config: [
                    {
                        darkIcon: darkBrowseIcon,
                        lightIcon: lightBrowseIcon,
                        iconAltText: 'Browse',
                        emphasisText: 'Browse',
                        // TODO: remove coming soon badge when content catalog is available
                        text: (
                            <>
                                catalog of workshops{' '}
                                <Badge color="blue">Coming soon!</Badge>
                            </>
                        ),
                    },
                    {
                        darkIcon: darkLaunchIcon,
                        lightIcon: lightLaunchIcon,
                        iconAltText: 'Launch',
                        emphasisText: 'Launch',
                        text: <>Workshop</>,
                    },
                    {
                        darkIcon: darkAwsAccountIcon,
                        lightIcon: lightAwsAccountIcon,
                        iconAltText: 'Use',
                        emphasisText: 'Use',
                        text: <>your own AWS Account</>,
                    },

                    {
                        darkIcon: darkCompleteIcon,
                        lightIcon: lightCompleteIcon,
                        iconAltText: 'Complete',
                        emphasisText: 'Complete',
                        text: <>the workshop</>,
                    },
                ],
            },
        },
        whatCanYouDoHere: {
            title: 'What can you do here',
            list: [
                {
                    detail:
                        'Join a scheduled customer engagement, such as a workshop, an Immersion Day, a bootcamp, an AWS GameDay, a hackathon, etc.',
                },
                // TODO: add back when content catalog is available
                // {
                //     detail:
                //         'Browse an extensive catalog of virtual workshops about various AWS topics and services',
                // },
                {
                    detail: 'Take a self-paced workshop',
                },
            ],
        },
    },
};

export default LandingPageConfig;
